import { useLoading } from "@/composables/useLoading";
import apiClient from "@/services/axios";
import { NavigationGuard, RouteLocationNormalized, RouteRecordRaw } from "vue-router";

type VisibilityEndpoint = (_to: RouteLocationNormalized) => string;

// UV6: a device or tenant the user does not see leads to the same notice
// whether or not it exists. The view itself is not mounted, so none of its
// data is loaded. Registered globally rather than as beforeEnter so it also
// runs on a switch between two devices and when the current view is
// re-entered after a role change (UV14).
export const visibleOrNotAuthorized: NavigationGuard = async (to) => {
  const endpoint = to.meta.visibleAt as VisibilityEndpoint | undefined;
  if (!endpoint) return true;

  const { showLoading, hideLoading } = useLoading();
  showLoading();
  try {
    const response = await apiClient.get(endpoint(to));
    // AV16: a tenant of which only the name is served answers without its identifier.
    return response?.data?.id ? true : { name: 'not-authorized' };
  } catch {
    return { name: 'not-authorized' };
  } finally {
    hideLoading();
  }
};

const privateRoutes: Array<RouteRecordRaw> = [
    {
      path: "/3dnjud7f",
      name: "system-status",
      components: { private: () => import("@/views/private/SystemStatusView.vue") },
      meta: { requiresAuth: true, requiresSystemStatus: true },
    },
    {
      path: "/admin",
      redirect: "/admin/tenants",
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
      },
    },
    {
      path: "/admin/devices",
      name: "admin-devices",
      components: { private: () => import("@/views/private/Admin/AdminDevicesView.vue") },
      meta: {
        titleKey: 'page_titles.admin',
        requiresAuth: true,
        requiresAdmin: true,
        requiresDeviceAdmin: true,
      },
    },
    {
      path: "/admin/tenants",
      name: "admin-tenants",
      components: { private: () => import("@/views/private/Admin/AdminTenantsView.vue") },
      meta: {
        titleKey: 'page_titles.admin',
        requiresAuth: true,
        requiresAdmin: true,
      },
    },
    {
      path: "/admin/tenants/:id",
      name: "admin-tenant-overview",
      components: { private: () => import("@/views/private/Admin/AdminTenantOverviewView.vue") },
      meta: {
        titleKey: 'page_titles.admin',
        requiresAuth: true,
        requiresAdmin: true,
        visibleAt: (to: RouteLocationNormalized) => `/tenants/${to.params.id}`,
      },
    },
    {
      path: "/admin/devices/:id",
      name: "admin-device-overview",
      components: { private: () => import("@/views/private/Admin/AdminDeviceOverviewView.vue") },
      meta: {
        titleKey: 'page_titles.admin',
        requiresAuth: true,
        requiresAdmin: true,
        requiresDeviceAdmin: true,
      },
    },
    {
      path: "/admin/users",
      name: "admin-users",
      components: { private: () => import("@/views/private/Admin/AdminUsersView.vue") },
      meta: {
        titleKey: 'page_titles.admin',
        requiresAuth: true,
        requiresAdmin: true,
        requiresUserAdmin: true,
      },
    },
    {
      path: "/admin/users/:id",
      name: "admin-user-overview",
      components: { private: () => import("@/views/private/Admin/AdminUserOverviewView.vue") },
      meta: {
        titleKey: 'page_titles.admin',
        requiresAuth: true,
        requiresAdmin: true,
        requiresUserAdmin: true,
      },
    },
    {
      path: "/plans",
      alias: "/plans",
      name: "plans",
      components: { private: () => import("@/views/private/PlansView.vue") },
      meta: { titleKey: 'page_titles.plans', requiresAuth: true },
    },
    {
      path: "/profile",
      alias: "/profile",
      name: "profile",
      components: { private: () => import("@/views/private/ProfileView.vue") },
      meta: { titleKey: 'page_titles.profile', requiresAuth: true },
    },
    {
      path: "/edit-name",
      alias: "/edit-name",
      name: "edit-name",
      components: { private: () => import("@/views/private/EditNameView.vue") },
      meta: { titleKey: 'page_titles.edit_name', requiresAuth: true },
    },
    {
      path: "/edit-email",
      alias: "/edit-email",
      name: "edit-email",
      components: { private: () => import("@/views/private/EditEmailView.vue") },
      meta: { titleKey: 'page_titles.edit_email', requiresAuth: true },
    },
    {
      path: "/edit-password",
      alias: "/edit-password",
      name: "edit-password",
      components: { private: () => import("@/views/private/EditPasswordView.vue") },
      meta: { titleKey: 'page_titles.edit_password', requiresAuth: true },
    },
    {
      path: "/edit-language",
      alias: "/edit-language",
      name: "edit-language",
      components: { private: () => import("@/views/private/EditLanguageView.vue") },
      meta: { titleKey: 'page_titles.edit_language', requiresAuth: true },
    },
    {
      path: "/edit-notifications",
      alias: "/edit-notifications",
      name: "edit-notifications",
      components: { private: () => import("@/views/private/EditNotificationsView.vue") },
      meta: { titleKey: 'page_titles.notifications', requiresAuth: true },
    },
    {
      path: "/help",
      alias: "/help",
      name: "help",
      components: { private: () => import("@/views/private/HelpView.vue") },
      meta: { titleKey: 'page_titles.help', requiresAuth: true },
    },
    {
      path: "/payments",
      alias: "/payments",
      name: "payments",
      components: { private: () => import("@/views/private/PaymentsView.vue") },
      meta: { titleKey: 'page_titles.payments', requiresAuth: true },
    },
    {
      path: "/settings",
      alias: "/settings",
      name: "settings",
      components: { private: () => import("@/views/private/SettingsView.vue") },
      meta: { titleKey: 'page_titles.settings', requiresAuth: true }
    },
    {
      path: "/home",
      alias: "/home",
      name: "home",
      components: { private: () => import("@/views/private/HomeView.vue") },
      meta: { titleKey: 'page_titles.home', requiresAuth: true },
    },
    {
      path: '/edit-device/:id',
      alias: '/edit-device/:id',
      name: 'edit-device',
      components: { private: () => import("@/views/private/EditDeviceView.vue") },
      meta: {
        titleKey: 'page_titles.edit_device',
        requiresAuth: true,
        visibleAt: (to: RouteLocationNormalized) => `/devices/${to.params.id}`,
      },
    },
    {
      path: '/device/:deviceId',
      name: 'device',
      components: { private: () => import("@/views/private/DeviceView.vue") },
      meta: {
        titleKey: 'page_titles.device',
        requiresAuth: true,
        visibleAt: (to: RouteLocationNormalized) => `/devices/${to.params.deviceId}`,
      },
    },
    {
      path: "/not-authorized",
      name: "not-authorized",
      components: { public: () => import("@/views/errors/NotAuthorizedView.vue") },
      // UV5: signed-in users are sent here too, so it is exempt from the
      // redirect of signed-in users away from public pages.
      meta: { titleKey: 'page_titles.not_authorized', requiresAuth: false, reachableSignedIn: true },
    },
  
  ];

  export default privateRoutes;